import { typography, radius } from '@/shared/theme/tokens';
import { BOARD, FLAP_FACE } from './boardTheme';

const MONO = BOARD.mono;

// Tile sizing presets. Each maps to a fontSize TOKEN (sizes are capped by the
// token scale; legibility comes from weight + the tall two-tone tile, not raw px).
const SIZES = {
  xl: { fontSize: typography.fontSize['7xl'], pad: '12px 9px', minW: 38, gap: 5 },
  lg: { fontSize: typography.fontSize['6xl'], pad: '9px 8px',  minW: 31, gap: 4 },
  md: { fontSize: typography.fontSize['4xl'], pad: '7px 7px',  minW: 25, gap: 4 },
  sm: { fontSize: typography.fontSize['2xl'], pad: '5px 6px',  minW: 19, gap: 3 },
};

/**
 * Split-flap / departures-board numeral. Renders each character as a two-tone
 * flap tile with a seam. A tile is keyed by its character, so when a digit
 * changes React remounts ONLY that tile and the flap-fall animation replays —
 * i.e. the flip means "this digit just changed", nothing more. Gated on
 * `animate`; under reduced-motion the numerals are static.
 */
export function SplitFlap({ value, color = BOARD.amber, size = 'lg', animate = false, dim = false }) {
  const s = SIZES[size] ?? SIZES.lg;
  const chars = String(value).split('');
  return (
    <span style={{ display: 'inline-flex', gap: s.gap, perspective: 700, perspectiveOrigin: 'center top' }}>
      {chars.map((ch, i) => (
        // Position-keyed on purpose: when the digit at a slot changes, only that
        // tile remounts and replays its flap-fall — the flip means "this changed".
        // eslint-disable-next-line react/no-array-index-key
        <Flap key={`${i}:${ch}`} ch={ch} s={s} color={dim ? BOARD.inkFaint : color} animate={animate} />
      ))}
    </span>
  );
}

function Flap({ ch, s, color, animate }) {
  return (
    <span style={{
      position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: s.minW, padding: s.pad,
      fontFamily: MONO, fontSize: s.fontSize, fontWeight: typography.fontWeight.extrabold,
      lineHeight: 1, color, fontVariantNumeric: 'tabular-nums',
      background: FLAP_FACE, borderRadius: radius.xs, border: `1px solid ${BOARD.seam}`,
      boxShadow: `inset 0 1px 0 ${BOARD.rule}, 0 1px 2px ${BOARD.wall}`,
      transformStyle: 'preserve-3d', transformOrigin: 'center top',
      animation: animate ? 'board-flap 0.44s cubic-bezier(0.34,0.9,0.32,1)' : 'none',
    }}>
      {/* center seam line */}
      <span aria-hidden style={{
        position: 'absolute', left: 2, right: 2, top: '50%', height: 1, marginTop: -0.5,
        background: BOARD.wall, opacity: 0.9,
      }} />
      <span style={{ position: 'relative' }}>{ch}</span>
    </span>
  );
}
